import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReferralInfoContext from './ReferralInfoContext';
import {
  getAffiliateCookie,
  parseAffiliateURL,
  setAffiliateCookie } from './parseAffiliateInfo';
import { fetchReferralInfo } from '../../api/storefront';
import removeCookie from '../../util/cookies';
// import useFeatureFlags from '../../hooks/use-feature-flags';

export default function ReferralInfoProvider({ children }) {
  const isLoading = useRef(false);

  /**
   * ReferralInfo is initially undefined.
   * This is used to indicate a pending request.
   * When referralInfo is defined, that means the request is finished.
   * It will be an empty object if no referralInfo.
   * It will be an object with more info if there are referralInfo.
   */
  const [referralInfo, setReferralInfo] = useState(undefined);
  const [affiliateInfo, setAffiliateInfo] = useState(undefined);

  /** We need to know if we need the old referral or the new */
  // const referralsFlag = (useFeatureFlags())['faster-friends'];

  const checkForGfReferralInfo = async () => {
    // Grab `kid` from query params and pass through to referral Info.
    const kid = new URLSearchParams(window.location.search).get('kid');

    return fetchReferralInfo(kid);
  }

  const checkForAffiliateInfo = () => {
    const existingAffiliate = getAffiliateCookie();
  
    if (existingAffiliate) {
      return existingAffiliate;
    }

    const info = parseAffiliateURL();
    setAffiliateCookie(info);
    return info;
  }

  /**
   * Load Once if needed
   */
  const ensureReferralInfo = useCallback(async () => {
    if (isLoading.current) return;
    isLoading.current = true;

    const gfReferral = await checkForGfReferralInfo();
    const affiliate = checkForAffiliateInfo();
    /* 
    * This will be replacing the old affiliate links
    * so we need to check if there was "referral" info
    * and if it's an old affiliate.
    */
   if (!gfReferral || gfReferral.isAffiliate) {
    // If there is an affiliate, we should set referral to empty
    // We also should remove the cookie
    if (affiliate && affiliate.name && affiliate.id) {
      setAffiliateInfo(affiliate);
      setReferralInfo(undefined);
      removeCookie('referral');
    } else {
      setReferralInfo(gfReferral);
      setAffiliateInfo(undefined);
      removeCookie('affiliate');
    }
   } else {
    setReferralInfo(gfReferral);
    setAffiliateInfo(undefined);
    removeCookie('affiliate');
   }
  }, []);

  // Referrals and affiliates have to stay separate for builder logic.
  return (
    <ReferralInfoContext.Provider
      value={useMemo(() => ({
        referralInfo,
        affiliateInfo,
        ensureReferralInfo
      }), [referralInfo, affiliateInfo, ensureReferralInfo])}>
      {children}
    </ReferralInfoContext.Provider>
  )
}

ReferralInfoProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
